import { Layout } from "antd";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router";

import WidgetsGrid from "@/components/Elements/WidgetsGrid";
import BaseHeader from "@/components/Header/BaseHeader";
import HeaderButtons from "@/components/Header/Organization";
import { PROJECTS_STATISTICS_WIDGETS } from "@/components/ListStatistics/Projects";
import ProjectsTable from "@/components/ListTable/ProjectsTable";
import { STORAGE_KEYS } from "@/constants/StorageKeys";
import { TAB_KEYS } from "@/constants/TabKeys";
import { LOCATIONS_FILTERS } from "@/entities/locations";
import { useGetOrganization } from "@/entities/organization";
import { PROJECTS_INCLUDES, PROJECTS_FILTERS } from "@/entities/projects";
import { ListProvider } from "@/modules/list";
import LocationAddButton from "@/modules/location/location-add-button";
import { getLocationCreateParentOrganization } from "@/modules/location/location-create";
import LocationsListProvider from "@/modules/locations/locations-list-provider";
import LocationsListTable from "@/modules/locations/locations-list-table";
import { isError404 } from "@/services/api";
import { ContextTabs } from "@/ui/context-tabs";

const { Content } = Layout;

const Details = () => {
  const { id } = useParams();
  const { t } = useTranslation();

  const { isLoading, organization, error, removeQuery } =
    useGetOrganization(id);

  const parentOrganization = getLocationCreateParentOrganization(organization);

  const tabs = [
    {
      key: TAB_KEYS.ORGANIZATIONS_LOCATIONS,
      label: t("global:locationPlural"),
      children: (
        <LocationsListProvider
          storageKey={STORAGE_KEYS.ORGANIZATIONS_LOCATIONS}
          contextTabValue={TAB_KEYS.ORGANIZATIONS_LOCATIONS}
          hiddenFilters={{ [LOCATIONS_FILTERS.ORGANIZATION_ID]: id }}
        >
          <LocationsListTable
            extraActions={
              <LocationAddButton
                parentOrganization={parentOrganization}
                shortButtonText
              />
            }
          />
        </LocationsListProvider>
      ),
    },
    {
      key: TAB_KEYS.ORGANIZATIONS_PROJECTS,
      label: t("global:projectPlural"),
      children: (
        <ListProvider
          model="projects"
          storageKey={STORAGE_KEYS.ORGANIZATIONS_PROJECTS}
          contextTabValue={TAB_KEYS.ORGANIZATIONS_PROJECTS}
          withMetadata={["statistics"]}
          includes={[PROJECTS_INCLUDES.USERS]}
          hiddenFilters={{ [PROJECTS_FILTERS.ORGANIZATION_ID]: id }}
        >
          <WidgetsGrid widgets={PROJECTS_STATISTICS_WIDGETS}>
            <ProjectsTable />
          </WidgetsGrid>
        </ListProvider>
      ),
    },
  ];

  if (isError404(error)) {
    removeQuery();
    return <Navigate to="/organizations" />;
  }

  return (
    <>
      <BaseHeader
        title={organization?.name}
        loading={isLoading}
        extra={
          !isLoading && [
            <HeaderButtons key="HeaderButtons" organization={organization} />,
          ]
        }
        logo={organization?.logo_document}
      />
      <Content>
        <ContextTabs defaultActiveKey="locations" items={tabs} />
      </Content>
    </>
  );
};

export default Details;
